import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Label } from '@abyss/web/ui/Label';
import { Text } from '@abyss/web/ui/Text';
// Add your Tooltip import here
import { Tooltip } from '@abyss/web/ui/Tooltip'; // Adjust import path as needed
import { getModuleOrDefault } from '@uhg-psx/mapping-files-translation-transperfect';
import find from 'lodash/find';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { PortalContext } from '../../../../context/PortalContext';
import { getLanguage } from '../../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  LastModifiedFilter,
  Option,
  ProviderSearchFilters,
} from '../../../../models/ProviderSearch';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
} from '../../../Constants';
import {
  getFilterValue,
  getPreviousDistanceOrDefaultOption,
} from '../../../Utils/filters.utils';
import { isPrioritySchedulingActive } from '../../BccFilterUtils';

interface Props {
  defaultSearchFilters: ProviderSearchFilters;
  lastModifiedFilter: LastModifiedFilter;
  selectedFilters: ProviderSearchFilters;
  onFilterSelectionChange: (
    data: ProviderSearchFilters,
    lastModifiedModel: string
  ) => void;
  setSelectedFiltersContainerHeight: (a: number) => void;
}

export const SelectedFilters = ({
  defaultSearchFilters,
  lastModifiedFilter,
  selectedFilters,
  onFilterSelectionChange,
  setSelectedFiltersContainerHeight,
}: Props) => {
  const { t } = useTranslation();
  const selectedFiltersRef = useRef<HTMLDivElement>(null);
  const coverageType = useCoverageType();

  const allConfigs = useLagoon('config');
  const defaultSearchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );
  const data = useLagoon('ui-messaging')();
  const tooltipContent = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.PLAN_TYPE_FILTER_TOOLTIP_CONTENT,
  });
  const [enhanceDistanceFilter, enableBHVirtualCareFilterV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
  ]);

  const selectedOptions = useMemo(() => {
    const options: {
      [key: string]: {
        label: string;
        type: string;
        value: string | number | boolean;
        dnt_label: string;
      };
    } = {};

    Object.keys(selectedFilters).forEach((model) => {
      let newEntryKey = '';
      if (Array.isArray(selectedFilters[model])) {
        selectedFilters[model].forEach((option: Option) => {
          if (option && !option.hide) {
            newEntryKey = `${model}_${option.label}_${option.value}`;
            options[newEntryKey] = {
              label: option?.label,
              type: model,
              value: option?.value,
              dnt_label: option?.dnt_label,
            };
          }
        });
      } else {
        const option: Option = selectedFilters[model];
        if (option?.value && !option?.hide) {
          newEntryKey = `${model}_${option.label}_${option.value}`;
          options[newEntryKey] = {
            label: option?.label,
            type: model,
            value: option?.value,
            dnt_label: option?.dnt_label,
          };
        }
      }
    });

    if (enhanceDistanceFilter) {
      // temporary fixed on EAP duplicates
      return Object.fromEntries(
        Object.entries(options).filter(
          ([, value]) =>
            ![
              FilterCollectionModel.DISTANCE,
              FilterCollectionModel.BEHAVIORAL_PROGRAM_INDICATOR,
            ].includes(value.type as FilterCollectionModel) &&
            !(
              FilterCollectionModel.AREA_OF_EXPERTISE === value.type &&
              value?.dnt_label === EAP_OPTION_LABEL
            )
        )
      );
    }

    return Object.fromEntries(
      Object.entries(options).filter(
        ([, value]) => value.type !== FilterCollectionModel.EAP_OFFERED
      )
    );
  }, [selectedFilters, enhanceDistanceFilter]);

  const { sectionType = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );

  const { portalData } = useContext(PortalContext);
  const portalLockEAPFilter = portalData?.portalConfig?.lockEapFilter;

  // Check if Priority Scheduling is active
  const isPrioritySchedulingEnabled =
    isPrioritySchedulingActive(selectedFilters);

  // Filters that should be disabled when Priority Scheduling is active
  const disabledFiltersWhenPriorityScheduling = [
    FilterCollectionModel.EAP_OFFERED,
    FilterCollectionModel.VIRTUAL_CARE,
    FilterCollectionModel.VIRTUAL_CARE_OPTIONS,
    FilterCollectionModel.UPCOMING_AVAILABILITY,
  ];

  // Check if a specific filter should be disabled based on Priority Scheduling
  const isFilterDisabledByPriorityScheduling = (filterType: string) =>
    isPrioritySchedulingEnabled &&
    disabledFiltersWhenPriorityScheduling.includes(
      filterType as FilterCollectionModel
    );

  const lockedPill = (option) =>
    portalLockEAPFilter && option?.dnt_label === EAP_OPTION_LABEL;

  const resetAllFiltersDataTestId: string = 'reset-all-filter';

  const handleResetFilter = () => {
    adobeLinkTrackEvent({
      name: resetAllFiltersDataTestId,
      location: `modal:${Constants.PROVIDER_RESULTS.ALL_FILTERS_DRAWER}:selected filters`,
    });

    const defaultSearchFiltersCopy = {
      ...defaultSearchFilters,
      [FilterCollectionModel.SORT_BY]: selectedFilters.SortBy,
    };

    onFilterSelectionChange(defaultSearchFiltersCopy, '');
  };

  const getDistanceDefaultSelection = (): Option[] | null => {
    const defaultDistance = defaultSearchFilters[
      FilterCollectionModel.DISTANCE
    ] as Option[] | undefined;

    if (!defaultDistance) {
      return null;
    }

    return defaultDistance.map((option: Option) => {
      if (!selectedFilters.AutoSearchRadius) {
        return { ...option };
      }

      return {
        ...option,
        value: selectedFilters.AutoSearchRadius,
        label: `${selectedFilters.AutoSearchRadius} ${
          selectedFilters.AutoSearchRadius > 1
            ? t('CONTENT_FILTERS.MILES')
            : t('CONTENT_FILTERS.MILE')
        }`,
        dnt_label: `${selectedFilters.AutoSearchRadius} miles`,
        c_value: selectedFilters.AutoSearchRadius,
      };
    });
  };

  const getArrayUpdatedSelection = (
    model: string,
    value: string | number | boolean,
    isVirtualCareFilterSelected: boolean,
    isBH: boolean
  ): Option[] | null => {
    const currentSelection = selectedFilters[model] as Option[];
    const updatedSelection: Option[] = currentSelection.filter(
      (option: Option) => option.value !== value
    );

    const shouldRestoreDefault =
      updatedSelection.length === 0 &&
      Object.keys(defaultSearchFilters).includes(model) &&
      !(isVirtualCareFilterSelected && (isBH || enhanceDistanceFilter));

    if (!shouldRestoreDefault) {
      return updatedSelection;
    }

    if (model === FilterCollectionModel.DISTANCE) {
      return getDistanceDefaultSelection();
    }

    return defaultSearchFilters[model] as Option[];
  };

  const getLastModifiedModel = (
    model: string,
    currentSelectedFilters: ProviderSearchFilters
  ): string => {
    const currentFilter = currentSelectedFilters[model];
    return model === lastModifiedFilter.model &&
      Array.isArray(currentFilter) &&
      currentFilter.length > 0
      ? model
      : '';
  };

  const handleRemoveFilter = (
    modelObject: {
      label: string;
      type: string;
      value: string | number | boolean;
      dnt_label: string;
    },
    label: string
  ) => {
    if (
      isFilterDisabledByPriorityScheduling(modelObject?.type) ||
      lockedPill(modelObject)
    ) {
      return;
    }

    const model = modelObject?.type;
    const value = modelObject?.value;

    adobeLinkTrackEvent({
      name: `${label}:remove`,
      location: `modal:${Constants.PROVIDER_RESULTS.ALL_FILTERS_DRAWER}:selected filters`,
    });

    const isVirtualCareFilterSelected = getFilterValue(
      selectedFilters,
      FilterCollectionModel.VIRTUAL_CARE
    );
    const isBH =
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];

    if (Array.isArray(selectedFilters[model])) {
      const updatedSelection = getArrayUpdatedSelection(
        model,
        value,
        isVirtualCareFilterSelected,
        isBH
      );

      if (!updatedSelection) {
        return;
      }

      const currentSelectedFilters = {
        ...selectedFilters,
        [model]: updatedSelection,
      };

      onFilterSelectionChange(
        currentSelectedFilters,
        getLastModifiedModel(model, currentSelectedFilters)
      );
      return;
    }

    const updatedSelection = {
      ...(selectedFilters[model] as Option),
      value: false,
    };

    const prioritySchedulingResetFilters =
      model === FilterCollectionModel.PRIORITY_SCHEDULING
        ? {
            [FilterCollectionModel.UPCOMING_AVAILABILITY]: [],
            [FilterCollectionModel.VIRTUAL_CARE_OPTIONS]: [],
          }
        : {};

    const currentSelectedFilters = {
      ...selectedFilters,
      [model]: updatedSelection,
      ...prioritySchedulingResetFilters,
      // If the coverage type is Behavioral Health and a previous distance value is available,
      // set the distance filter to the previous value when the virtual care filter is cleared
      ...getPreviousDistanceOrDefaultOption(
        enhanceDistanceFilter,
        coverageType,
        model as FilterCollectionModel,
        selectedFilters,
        enableBHVirtualCareFilterV2,
        sectionType,
        defaultSearchRadius
      ),
    };

    onFilterSelectionChange(
      currentSelectedFilters,
      getLastModifiedModel(model, currentSelectedFilters)
    );
  };

  const getTranslations = async (translationFile, filter) => {
    const localeCode = getLanguage()?.code ?? 'en';
    const localizedContent = await getModuleOrDefault(
      localeCode,
      translationFile
    );
    const normalizedValue = filter.value?.toLowerCase();
    return (
      filter.label ??
      localizedContent?.[filter.value] ??
      localizedContent?.[normalizedValue] ??
      localizedContent?.[filter.value?.toUpperCase()]
    );
  };
  const getSelectedFilterLabel = async (filter) => {
    const staticTranslations = {
      [FilterCollectionModel.ACCEPTING_NEW_PATIENTS]:
        'CONTENT_FILTERS.ACCEPTING_NEW_PATIENTS',
      [FilterCollectionModel.ESSENTIAL_COMMUNITY_PROVIDER]:
        'CONTENT_FILTERS.ESSENTIAL_COMMUNITY_PROVIDER',
      [FilterCollectionModel.BOARD_CERTIFIED_SPECIALITY_INDICATOR]:
        'CONTENT_FILTERS.BOARD_CERTIFIED',
      [FilterCollectionModel.PREMIUM_CARE_PHYSICIAN]:
        'CONTENT_FILTERS.ALL_FILTERS_DRAWER.PREMIUM_CARE_PHYSICIAN',
      [FilterCollectionModel.VIRTUAL_CARE]: 'CONTENT_FILTERS.VIRTUAL_CARE',
    };
    if (staticTranslations[filter.type]) {
      return t(staticTranslations[filter.type]);
    }
    switch (filter.type) {
      case FilterCollectionModel.DISTANCE:
        return (
          filter.value +
          ' ' +
          `${
            filter.value > 1
              ? t('CONTENT_FILTERS.MILES')
              : t('CONTENT_FILTERS.MILE')
          }`
        );
      case FilterCollectionModel.CULTURAL_COMPETENCE:
        return await getTranslations('culturalCompetencyTranslations', filter);
      case FilterCollectionModel.ACCESSIBILITY_CODE:
        return await getTranslations('accessibilityTranslations', filter);
      case FilterCollectionModel.LANGUAGE_CODE:
        return await getTranslations('languageTranslations', filter);
      case FilterCollectionModel.AREA_OF_EXPERTISE:
        return await getTranslations('aoeTranslations', filter);
      case FilterCollectionModel.ACCREDITATION:
        return await getTranslations('accreditationTranslations', filter);
      case FilterCollectionModel.ETHNICITY_CODE:
        return await getTranslations('ethnicityTranslations', filter);
      case FilterCollectionModel.VIRTUAL_CARE_OPTIONS:
        return t(`FILTER_OPTIONS.VirtualCareOptions.${filter.value}`);
      case FilterCollectionModel.HEALTH_SUPPLIES_OFFERED:
        return await getTranslations('facilityServiceCodeTranslations', filter);
      case FilterCollectionModel.FACILITY_TYPE:
        return await getTranslations(
          'facilitySubCategoryCodeTranslations',
          filter
        );
      case FilterCollectionModel.SERVICES_OFFERED:
        return await getTranslations('servicesOfferedTranslations', filter);
      case FilterCollectionModel.PATIENT_REVIEWS:
        const reviewCount = filter.label.replace(/ /g, '_').toUpperCase();
        return reviewCount == '5_STARS'
          ? t(`CONTENT_FILTERS.${reviewCount}`)
          : `${filter.value} ${t('CONTENT_FILTERS.STARS_AND_UP')}`;
      default:
        return t(`${filter.label}`) || filter.label;
    }
  };
  useEffect(() => {
    if (selectedFilters) {
      setSelectedFiltersContainerHeight(0);
    }
  }, [selectedOptions]);

  const planTypeCount = Object.values(selectedOptions).filter(
    (opt) => opt.type === 'PlanType'
  ).length;

  const [filterLabels, setFilterLabels] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    const fetchLabels = async () => {
      const labels = await generateLabels(
        selectedOptions,
        getSelectedFilterLabel
      );

      setFilterLabels((prevLabels) => {
        const isEqual = areLabelsEqual(labels, prevLabels);
        return isEqual ? prevLabels : labels;
      });
    };

    fetchLabels();
  }, [selectedOptions, getSelectedFilterLabel]);

  const generateLabels = async (options, getLabel) => {
    const labels = {};
    for (const [key, filter] of Object.entries(options)) {
      labels[key] = await getLabel(filter);
    }
    return labels;
  };

  const areLabelsEqual = (newLabels, oldLabels) =>
    Object.keys(newLabels).every((key) => newLabels[key] === oldLabels[key]);

  const spanRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [truncatedItems, setTruncatedItems] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    const truncated: Record<string, boolean> = {};
    Object.keys(spanRefs.current).forEach((key) => {
      const el = spanRefs.current[key];
      if (el) {
        truncated[key] = el.scrollWidth > el.clientWidth;
      }
    });
    setTruncatedItems(truncated);
  }, [filterLabels]);

  // --- FIXED: Render filter chips with tooltip logic ---
  const filterChips = Object.entries(selectedOptions).map(([item, info]) => {
    const showPlanTypeTooltip = info.type === 'PlanType' && planTypeCount === 1;

    const chip = (
      <Text
        aria-label={`${filterLabels[item] || ''} ${t(
          'CONTENT_FILTERS.DISMISS_FILTER'
        )}`}
        css={{
          'abyss-text-root': {
            height: '36px',
            padding: '6px $sm 6px 14px',
            border: '1px solid var(--abyss-psx-parcel-colors-gray8)',
            color: 'var(--abyss-psx-parcel-colors-gray8)',
            borderRadius: '90px',
            display: 'flex',
            alignItems: 'center',
            ...(lockedPill(info) && {
              backgroundColor: 'var(--abyss-psx-parcel-colors-gray2)',
              '&,.abyss-label-root': {
                cursor: 'not-allowed',
              },
            }),
            ...(isFilterDisabledByPriorityScheduling(info.type) && {
              backgroundColor: 'var(--abyss-psx-parcel-colors-gray2)',
              '&,.abyss-label-root': {
                cursor: 'not-allowed',
              },
            }),
          },
          'abyss-button-content-container': {
            gap: '$sm',
          },
        }}
        data-auto-testid="selected-filter-chip"
        data-testid="selected-filter-chip"
        key={item}
        onClick={() => handleRemoveFilter(info, item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleRemoveFilter(info, item);
          }
        }}
        role="button"
        tabIndex={0}
        variant="custom"
      >
        <Label fontWeight="$medium">
          <span
            ref={(el) => {
              spanRefs.current[item] = el;
            }}
            style={{
              display: 'block',
              maxWidth: '330px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {filterLabels[item] || info.label}
          </span>
        </Label>
        {!lockedPill(info) &&
          !showPlanTypeTooltip &&
          !isFilterDisabledByPriorityScheduling(info.type) && (
            <IconMaterial
              aria-label="close-icon"
              color="$descriptionBlack"
              css={{
                cursor: 'pointer',
              }}
              data-auto-testid={`${item}-clear-filter`}
              data-testid={`${item}-clear-filter`}
              icon="close"
              onClick={() => handleRemoveFilter(info, item)}
            />
          )}
      </Text>
    );

    const fullLabel = filterLabels[item] || info.label;
    const showTruncatedTooltip = truncatedItems[item];

    if (!showPlanTypeTooltip && !showTruncatedTooltip) {
      return chip;
    }

    return (
      <Tooltip
        content={showPlanTypeTooltip ? tooltipContent?.message : fullLabel}
        key={item}
      >
        {chip}
      </Tooltip>
    );
  });

  const filteredSelectedOptions = Object.fromEntries(
    Object.entries(selectedOptions).filter(([, value]) => !lockedPill(value))
  );

  return (
    <div
      ref={selectedFiltersRef}
      style={{
        padding: '0px',
        margin: '0px',
        width: '100%',
        backgroundColor: 'white',
        zIndex: '999',
        position: 'static',
        left: 0,
        top: 48,
        borderBottom: '1px solid var(--abyss-psx-parcel-colors-gray4)',
        paddingBottom: '24px',
      }}
    >
      {Object.keys(selectedOptions).length > 0 ? (
        <Flex
          aria-label="Filters"
          css={{
            padding: '$lg $md 0 15px',
            gap: '$sm',
          }}
          role="group"
        >
          <Flex
            css={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Text data-testid="filters-heading" fontWeight="$bold">
              {' '}
              {t('CONTENT_FILTERS.FILTERS')}:
            </Text>
            {Object.keys(filteredSelectedOptions).length > 0 && (
              <Text
                color="$interactive1"
                css={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                data-auto-testid={resetAllFiltersDataTestId}
                data-testid={resetAllFiltersDataTestId}
                fontWeight="$bold"
                onClick={handleResetFilter}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleResetFilter();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {t('CONTENT_FILTERS.RESET_ALL')}
              </Text>
            )}
          </Flex>
          {filterChips}
        </Flex>
      ) : null}
    </div>
  );
};
